import { useEffect, useRef, useState } from 'react';
import type { PrivateView, PublicView, RoleId } from '@ringmaster/shared';
import { ROLE_META } from '@ringmaster/shared';
import { Glyph } from '../ui/Glyph.js';
import { ParticleLayer } from '../fx/particles/ParticleLayer.js';
import { ROLE_GLYPH, ROLE_TONE, roleAccent } from './roleGlyph.js';
import plaqueDead from '../assets/ceremony/plaque-dead.webp';

/**
 * DeadScreen — the dead player's own phone. This is the ONE screen sanctioned to
 * turn blood-red (the death-visual rule: only the player who died goes red;
 * everyone else stays calm near-black).
 *
 * Entry: a single ~300ms full-bleed blood flash (photosensitivity-safe: one
 * pulse, no strobe) settling into a deep blood gradient. Under reduced motion the
 * flash is an instant cut (no pulse). Then: "YOU ARE DEAD" in Grenze bone-white
 * with a skull glyph, the player's own role on a small desaturated blood-rimmed
 * card, a slow fall of ash, and — when a full-truth roster is available — the
 * spectator ledger.
 *
 * NOTE on the ledger: the dead do NOT learn everyone's roles mid-game, so the
 * engine only ever populates `deadTruth` at END. But END renders EndScreen
 * (HandGameView routes phase==='END' before the dead branch), never DeadScreen,
 * so in practice `privateView.deadTruth` is always null here and the ledger
 * block below is currently unreachable. It is kept null-safe on purpose: it
 * costs nothing, and if the routing ever changes (or a future ledger is fed by
 * public reveals) it degrades gracefully rather than crashing.
 */
export function DeadScreen({
  privateView,
  publicView,
}: {
  privateView: PrivateView;
  publicView: PublicView;
}) {
  const meta = ROLE_META[privateView.role];
  const myRole = privateView.role as RoleId;
  const deadTruth = privateView.deadTruth;

  // ── Entry blood flash (one pulse; instant cut under reduced motion) ──────────
  const flashRef = useRef<HTMLDivElement>(null);
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const el = flashRef.current;
    if (!el) return;
    if (mq.matches) {
      // Reduced motion: no pulse — the screen is simply already at rest.
      el.style.opacity = '0';
      return;
    }
    // Full blood flash → fade out over 300ms (mirrors ShakeContainer.flash).
    el.style.opacity = '1';
    el.style.transition = 'none';
    // Force reflow so the transition retriggers from full opacity.
    void el.offsetHeight;
    el.style.transition = 'opacity 300ms ease-out';
    el.style.opacity = '0';
  }, []);

  const enriched = publicView.players.map((p) => ({
    ...p,
    revealedRole: (deadTruth?.[p.id] ?? p.revealedRole) as RoleId | undefined,
    alive: p.alive,
  }));

  return (
    <div
      className="game-screen"
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        // The dead player's OWN screen carries the blood-red identity.
        background: 'linear-gradient(180deg, #3a0006 0%, #12060a 55%, #080808 100%)',
        color: 'var(--text-primary)',
        padding: 'var(--space-5)',
        paddingTop: 'max(var(--space-7), env(safe-area-inset-top, 0px) + var(--space-5))',
        gap: 'var(--space-5)',
        overflow: 'hidden',
      }}
    >
      <DeadStyles />

      {/* Slow ash fall over the whole screen (phone-tier capped ≤40). */}
      <ParticleLayer preset="ash" intensity={0.7} slot={0} />

      {/* One-shot blood flash overlay. */}
      <div ref={flashRef} aria-hidden className="dead-flash" />

      {/* Header */}
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Glyph name="skull" size={52} tone="blood" />
        </div>
        <h1 className="dead-title">YOU ARE DEAD</h1>
        <p style={{ color: 'var(--text-tertiary)', fontSize: 14, margin: 0 }}>
          Your part is played. Watch the rest unfold &mdash; the truth waits for the end.
        </p>
      </div>

      {/* Own role — small, desaturated, blood-rimmed card */}
      <div className="dead-rolecard" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ color: 'var(--text-tertiary)', fontSize: 12, marginBottom: 'var(--space-2)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Your role was
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', filter: 'saturate(0.55)' }}>
          <Glyph name={ROLE_GLYPH[myRole]} size={38} tone={ROLE_TONE[myRole]} />
        </div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginTop: 'var(--space-2)' }}>{meta.label}</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 'var(--space-2)' }}>{meta.tagline}</div>
      </div>

      {/* The Ringmaster's ledger — every role laid bare */}
      {deadTruth && (
        <div className="dead-ledger" style={{ position: 'relative', zIndex: 1 }}>
          <div className="dead-ledger-head">
            <Glyph name="scroll" size={16} tone="gold" />
            <span>The Ringmaster&rsquo;s ledger</span>
          </div>
          <div>
            {enriched.map((p) => {
              const role = p.revealedRole;
              const isMe = p.id === privateView.playerId;
              return (
                <div key={p.id} className={`dead-ledger-row${p.alive ? '' : ' is-dead'}`}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    {role ? (
                      <Glyph name={ROLE_GLYPH[role]} size={18} tone={ROLE_TONE[role]} />
                    ) : (
                      <Glyph name="closedEye" size={18} tone="bone" />
                    )}
                    <span className="dead-ledger-name">
                      {p.name}
                      {isMe && <span style={{ color: 'var(--text-tertiary)', fontSize: 12, marginLeft: 6 }}>(you)</span>}
                    </span>
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: 14,
                      color: role ? roleAccent(role) : 'var(--text-tertiary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {role ? role.charAt(0) + role.slice(1).toLowerCase() : '—'}
                    {!p.alive && <span style={{ color: 'var(--state-dead, #9c8f86)', marginLeft: 8, fontSize: 12 }}>fallen</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Phase indicator */}
      <div className="dead-phase" style={{ position: 'relative', zIndex: 1 }}>
        <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>
          Game continues — {publicView.phase.replace('_', ' ')} (Round {publicView.round})
        </span>
      </div>
    </div>
  );
}

function DeadStyles() {
  return (
    <style>{`
      .dead-flash {
        position: absolute; inset: 0; z-index: 40; pointer-events: none;
        background: radial-gradient(120% 100% at 50% 40%, #c1000f 0%, #6a0008 60%, #3a0006 100%);
        opacity: 0;
      }
      .dead-title {
        font-family: var(--font-display); font-size: 40px; line-height: 1;
        margin: var(--space-3) 0 var(--space-2);
        color: var(--gold-6, #f7e3b0);
        text-shadow: 0 0 26px rgba(153,0,0,0.65), 0 2px 6px rgba(0,0,0,0.7);
        letter-spacing: 0.04em;
      }
      .dead-rolecard {
        background: linear-gradient(180deg, rgba(20,6,10,0.7) 0%, rgba(8,8,8,0.55) 100%);
        border: 1px solid var(--blood-3, #802636);
        border-radius: var(--radius-card); padding: var(--space-5); text-align: center;
        box-shadow: var(--rim-blood, 0 0 24px rgba(153,0,0,0.55));
      }
      .dead-ledger {
        flex: 1;
        background:
          linear-gradient(180deg, rgba(20,12,7,0.82) 0%, rgba(11,7,5,0.9) 100%),
          url(${plaqueDead}) center/cover;
        border: 1px solid var(--gold-2, #7a4c1e);
        border-radius: var(--radius-card); padding: var(--space-5);
        box-shadow: var(--shadow-card);
      }
      .dead-ledger-head {
        display: flex; align-items: center; gap: 8px;
        color: var(--gold-5, #e6b866); font-size: 12px; font-weight: 600;
        text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: var(--space-4);
      }
      .dead-ledger-row {
        display: flex; align-items: center; justify-content: space-between; gap: var(--space-3);
        padding: 10px 0; border-bottom: 1px solid rgba(230,184,102,0.14);
        font-family: var(--font-ui); font-size: 15px; color: rgba(255,255,255,0.9);
      }
      .dead-ledger-row:last-child { border-bottom: none; }
      .dead-ledger-row.is-dead { opacity: 0.55; }
      .dead-ledger-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .dead-phase {
        background: rgba(0,0,0,0.4); border-radius: var(--radius-md);
        padding: var(--space-3) var(--space-4); text-align: center;
      }
      @media (prefers-reduced-motion: reduce) {
        .dead-flash { transition: none !important; }
      }
    `}</style>
  );
}
